<template>
  <div :class="cssClasses" v-click-outside="onClickOutside">

    <div
      :class="[$style.toggle, 'cursor-pointer']"
      @click="toggle">
      <slot
        name="toggle"
        :is-opened="isOpened"
      />
    </div>

    <div
      :class="cssContentClasses"
      v-if="isOpened"
    >
      <b-scrollable
        :style="scrollableCssStyle"
        :class="$style.scrollable"
        @scroll="$emit('scroll', $event)"
      >
        <slot/>
      </b-scrollable>
    </div>

  </div>
</template>

<script>
/*
 *
 * b-dropdown component
 *
 * 1. implements (optional) v-model interface
 *
 * 2. emits:
 *    - input & change on toggle
 *    - open
 *    - close
 */
import { is } from 'ramda'

import ClickOutside from '../../directives/click-outside'
import BScrollable from '../b-scrollable/index.vue'

// themeable mixin factory
import makeThemeable from '../../utils/make-themeable'

const POSITION_TYPES = [
  'bottom-left',
  'bottom-right',
  'bottom-center',
  'top-left',
  'top-right',
  'top-center',
  'right-top',
  'left-top',
  'left-bottom',
  'right-bottom',
  'right-down',
  'left-down',
]

const Position = makeThemeable(POSITION_TYPES, 'position')

export default {

  emits: [
    'change',
    'input',
    'scroll',
    'open',
    'close',
  ],
  name: 'b-dropdown',

  mixins: [
    Position,
  ],

  directives: {
    ClickOutside,
  },

  components: {
    BScrollable,
  },

  props: {

    value: {
      type: Boolean,
      default: null,
    },

    inline: {
      type: Boolean,
      default: false,
    },

    showShadow: {
      type: Boolean,
      default: true,
    },

    clickOutsideCloses: {
      type: Boolean,
      default: true,
    },

    /*
     * (scrollable) height
     */
    height: {
      type: [Number, String],
      default: 'auto',
    },
    disabled: Boolean,
    manualToggle: Boolean,

    contentFullWidth: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      opened: false,
    }
  },

  computed: {

    isOpened () {
      // shortcut: `value` takes precedense over internal state
      if (is(Boolean, this.value)) return this.value

      // if `value` is not set, use internal state
      return this.opened
    },

    cssClasses () {
      return [
        'relative',
        this.showShadow && this.$style['has--shadow'],
        this.inline
          ? this.$style['is--inline']
          : this.$style['is--overlay'],
      ]
    },

    cssContentClasses () {
      return [
        this.$style.content,
        this.$style.position,
        // @see themeable mixin (factory)
        this.position__cssClasses,
        this.contentFullWidth && this.$style['is--content-full-width'],
      ]
    },

    scrollableCssStyle () {
      const height = is(Number, this.height)
        ? `${this.height}px`
        : this.height

      return `max-height: ${height}`
    },

    contentCssStyle () {
      // shortcut: dropdown is closed!
      if (!this.isOpened) return {}

      const attribute = this.inline ? 'margin-top' : 'top'
      return `${attribute}: ${this.offset}px`
    },
  },

  methods: {

    /*
     *
     * public interface
     *
     */
    toggle () {
      if (this.disabled || this.manualToggle) return
      this.setOpened(!this.opened)
    },

    open () {
      if (this.disabled) return
      this.setOpened(true)
    },

    close () {
      this.setOpened(false)
    },

    /*
     *
     * private
     *
     */
    setOpened (opened, emitInput = true) {
      if (this.disabled) return
      this.opened = opened
      if (emitInput) {
        this.emitOpened()
      }
    },

    emitOpened () {
      if (this.disabled) return
      const opened = this.opened
      this.$emit('input', opened)
      this.$emit('change', opened)

      const e = opened ? 'open' : 'close'
      this.$emit(e)
    },

    onClickOutside () {
      if (this.clickOutsideCloses && this.opened) {
        this.close()
      }
    },
  },

  created () {
    // if `value` is set...
    if (is(Boolean, this.value)) {
      // sync. `opened` with `value`
      this.setOpened(this.value, false)
    } else {
      // sync. `value` with `opened`
      this.emitOpened()
    }
  },

  watch: {

    value: function (value, previous) {
      // if `value` is set...
      if (is(Boolean, value)) {
        // sync. `opened` with `value`
        this.setOpened(value, false)
      }

      /*
       * todo: when `value` changes from Boolean to `null` or `undefined`
       *       internal state should be sync. with previous value...
       */
    },
  },
}
</script>

<style lang="scss" module>
@layer legacy-views {

$verticalPadding: 16px;
$horizontalPadding: 1.5rem;

.content {
  background: var(--color-bg-primary);
  border-radius: 8px;
  padding: $verticalPadding 0;
  min-width: 100%;

  .has--shadow & {
    box-shadow: 0 1rem 48px 0 rgba(62, 53, 71, .18);
    filter: drop-shadow(0 0 1px $color-gray-14);
  }
}

.position {
  &:before {
    content: '';
    width: 0;
    height: 0;
    position: absolute;
    border-style: solid;
  }
  &--bottom-left {
    top: calc(100% + 12px); // 12px of 'before' height
    left: 0;

    &:before {
      top: -11px;
      left: 16px;
      border-width: 0 10px 12px 10px;
      border-color: transparent transparent var(--color-white) transparent;
    }
  }
  &--bottom-right {
    top: calc(100% + 12px); // 12px of 'before' height
    right: 0;

    &:before {
      top: -11px;
      right: 16px;
      border-width: 0 10px 12px 10px;
      border-color: transparent transparent var(--color-white) transparent;
    }
  }
  &--bottom-center {
    top: calc(100% + 12px); // 12px of 'before' height
    left: 50%;
    transform: translate(-50%, 0);

    &:before {
      top: -11px;
      left: 50%;
      transform: translate(-50%, 0);
      border-width: 0 10px 12px 10px;
      border-color: transparent transparent var(--color-white) transparent;
    }
  }
  &--top-left {
    bottom: calc(100% + 12px); // 12px of 'before' height
    left: 0;

    &:before {
      bottom: -11px;
      left: 16px;
      border-width: 12px 10px 0 10px;
      border-color: var(--color-white) transparent transparent transparent;
    }
  }
  &--top-right {
    bottom: calc(100% + 12px); // 12px of 'before' height
    right: 0;

    &:before {
      bottom: -11px;
      right: 16px;
      border-width: 12px 10px 0 10px;
      border-color: var(--color-white) transparent transparent transparent;
    }
  }
  &--top-center {
    bottom: calc(100% + 12px); // 12px of 'before' height
    left: 50%;
    transform: translate(-50%, 0);

    &:before {
      bottom: -11px;
      left: 50%;
      transform: translate(-50%, 0);
      border-width: 12px 10px 0 10px;
      border-color: var(--color-white) transparent transparent transparent;
    }
  }
  &--right-top {
    top: 0;
    left: calc(100% + 12px); // 12px of 'before' height

    &:before {
      top: 11px;
      left: -12px;
      border-width: 10px 12px 10px 0;
      border-color: transparent var(--color-white) transparent transparent;
    }
  }
  &--left-top {
    top: 0;
    right: calc(100% + 12px); // 12px of 'before' height

    &:before {
      top: 11px;
      right: -12px;
      border-width: 10px 0 10px 12px;
      border-color: transparent transparent transparent var(--color-white);
    }
  }
  &--right-bottom {
    bottom: 0;
    left: calc(100% + 12px); // 12px of 'before' height

    &:before {
      bottom: 11px;
      left: -12px;
      border-width: 10px 12px 10px 0;
      border-color: transparent var(--color-white) transparent transparent;
    }
  }
  &--left-bottom {
    bottom: 0;
    right: calc(100% + 12px); // 12px of 'before' height

    &:before {
      bottom: 11px;
      right: -12px;
      border-width: 10px 0 10px 12px;
      border-color: transparent transparent transparent var(--color-white);
    }
  }
  &--right-down {
    top: calc(100% - 31px); // 31px of before position and height
    left: calc(100% + 12px); // 12px of 'before' height

    &:before {
      top: 11px;
      left: -12px;
      border-width: 10px 12px 10px 0;
      border-color: transparent var(--color-white) transparent transparent;
    }
  }
  &--left-down {
    top: calc(100% - 31px); // 31px of before position and height
    right: calc(100% + 12px); // 12px of 'before' height

    &:before {
      top: 11px;
      right: -12px;
      border-width: 10px 0 10px 12px;
      border-color: transparent transparent transparent var(--color-white);
    }
  }
}

.scrollable {
  padding: 0 $horizontalPadding;
  max-height: 12rem;
}

.is--overlay {
  .content {
    position: absolute;
    z-index: 100;
  }
}

.is--inline {
  .content {
    position: relative;
  }
}

.is--content-full-width {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
}

}</style>
